import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { documentService } from "../../services/documentService";
import Icon from "../../components/Common/Icon";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

const CATEGORIES = [
  { value: "KILAVUZ", label: "Kılavuz" },
  { value: "SARTNAME", label: "Şartname" },
  { value: "DUYURU", label: "Duyuru" },
  { value: "DIGER", label: "Diğer" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.csv";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }) : "—";

const fmtSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const iconForType = (ct = "") => {
  if (ct.includes("pdf")) return "picture_as_pdf";
  if (ct.includes("word")) return "description";
  if (ct.includes("sheet") || ct.includes("excel") || ct.includes("csv")) return "table_chart";
  if (ct.includes("image")) return "image";
  return "insert_drive_file";
};

/**
 * Sayfa içi önizleme türü. Word/Excel tarayıcıda gömülü açılamadığından yalnızca
 * PDF, görsel ve metin (CSV) önizlenir; diğerlerinde önizleme butonu gizlenir.
 */
const previewKind = (ct = "") => {
  if (ct.includes("pdf")) return "pdf";
  if (ct.includes("image")) return "image";
  if (ct.includes("csv") || ct.includes("text/plain")) return "text";
  return null;
};

const emptyUpload = { file: null, title: "", description: "", category: "DIGER" };

/* ------------------------------------------------------------ Döküman satırı */

function DocRow({ doc, onView, onDownload, onEdit, onDelete, downloading, viewing }) {
  const canPreview = !!previewKind(doc.contentType);
  return (
    <div className="flex items-center gap-3 px-4 py-3.5 border-b border-outline-variant/60 hover:bg-surface-container-low/50 transition group">
      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Icon name={iconForType(doc.contentType)} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate text-on-surface">{doc.title}</p>
          {!doc.active && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-surface-container-highest text-on-surface-variant shrink-0">
              PASİF
            </span>
          )}
        </div>
        <p className="text-xs text-on-surface-variant truncate">
          {doc.fileName} · {fmtSize(doc.sizeBytes)}
          {doc.category && ` · ${CATEGORY_LABEL[doc.category] ?? doc.category}`}
        </p>
      </div>

      <span className="hidden md:block text-xs text-on-surface-variant shrink-0">{fmtDate(doc.createdAt)}</span>

      {canPreview && (
        <button
          onClick={() => onView(doc)}
          disabled={viewing}
          title="Görüntüle"
          className="p-2 rounded-lg text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition shrink-0 disabled:opacity-40"
        >
          <Icon name="visibility" className="!text-[18px]" />
        </button>
      )}

      <button
        onClick={() => onDownload(doc)}
        disabled={downloading}
        title="İndir"
        className="p-2 rounded-lg text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition shrink-0 disabled:opacity-40"
      >
        <Icon name="download" className="!text-[18px]" />
      </button>

      {doc.editable && (
        <>
          <button
            onClick={() => onEdit(doc)}
            title="Düzenle"
            className="p-2 rounded-lg text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition shrink-0"
          >
            <Icon name="edit" className="!text-[18px]" />
          </button>
          <button
            onClick={() => onDelete(doc)}
            title="Sil"
            className="p-2 rounded-lg text-on-surface-variant hover:bg-error/10 hover:text-error transition shrink-0"
          >
            <Icon name="delete" className="!text-[18px]" />
          </button>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- Sayfa */

export default function DocumentsPage() {
  const { showToast } = useToast();
  const user = useSelector((state) => state.auth.user);
  const role = user?.role;
  const isAdmin = role === "ADMIN";
  const isManager = role === "MANAGER";

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(null); // { limit, used, remaining }
  const [tab, setTab] = useState(isAdmin ? "SALON" : "PLATFORM"); // admin sadece platform görür
  const [downloadingId, setDownloadingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [viewer, setViewer] = useState(null); // { doc, kind, url?, text? }

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState(emptyUpload);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const fileInputRef = useRef(null);

  const [editing, setEditing] = useState(null); // döküman
  const [editForm, setEditForm] = useState({ title: "", description: "", category: "DIGER", active: true });

  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  const loadLimit = () => {
    if (!isManager) return;
    documentService.limit().then((r) => setLimit(r.data?.data ?? null)).catch(() => {});
  };

  const load = () => {
    setLoading(true);
    documentService
      .list()
      .then((res) => setDocs(res.data?.data ?? []))
      .catch(() => showToast("Dökümanlar yüklenemedi.", "error"))
      .finally(() => setLoading(false));
    loadLimit();
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const platformDocs = useMemo(() => docs.filter((d) => d.scope === "PLATFORM"), [docs]);
  const salonDocs = useMemo(() => docs.filter((d) => d.scope === "SALON"), [docs]);
  const shown = tab === "PLATFORM" ? platformDocs : salonDocs;

  const limitReached = limit && limit.limit != null && limit.remaining <= 0;

  // Yükleme yetkisi: ADMIN platform dökümanı yükler (tek liste), MANAGER salon
  // sekmesinde kendi salonuna yükler. STAFF ve platform sekmesi salt-okunur.
  const showUploadButton = isAdmin || (isManager && tab === "SALON");

  const openUpload = () => {
    if (isManager && limitReached) {
      showToast(`Döküman limitiniz doldu (${limit.limit}). Paketinizi yükseltin.`, "error");
      return;
    }
    setUploadForm(emptyUpload);
    setFormError(null);
    setUploadOpen(true);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0] ?? null;
    setUploadForm((prev) => ({
      ...prev,
      file: f,
      // Başlık boşsa dosya adından (uzantısız) öner.
      title: prev.title || (f ? f.name.replace(/\.[^.]+$/, "") : ""),
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!uploadForm.file) { setFormError("Lütfen bir dosya seçin."); return; }
    if (!uploadForm.title.trim()) { setFormError("Başlık zorunludur."); return; }

    const fd = new FormData();
    fd.append("file", uploadForm.file);
    fd.append("title", uploadForm.title.trim());
    if (uploadForm.description.trim()) fd.append("description", uploadForm.description.trim());
    fd.append("category", uploadForm.category);

    setSaving(true);
    try {
      await documentService.upload(fd);
      showToast("Döküman yüklendi.", "success");
      setUploadOpen(false);
      load();
    } catch (err) {
      setFormError(err.response?.data?.error ?? "Yükleme başarısız.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const res = await documentService.download(doc.id);
      const blob = new Blob([res.data], { type: doc.contentType || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileName || "dokuman";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast("İndirme başarısız.", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  // Sayfa içi görüntüleme: dosyayı blob olarak çekip önizleme türüne göre gösterir.
  const handleView = async (doc) => {
    const kind = previewKind(doc.contentType);
    if (!kind) { handleDownload(doc); return; }
    setViewingId(doc.id);
    try {
      const res = await documentService.download(doc.id);
      const blob = new Blob([res.data], { type: doc.contentType || "application/octet-stream" });
      if (kind === "text") {
        const text = await blob.text();
        setViewer({ doc, kind, text });
      } else {
        const url = URL.createObjectURL(blob);
        setViewer({ doc, kind, url });
      }
    } catch {
      showToast("Döküman görüntülenemedi.", "error");
    } finally {
      setViewingId(null);
    }
  };

  const closeViewer = () => {
    setViewer((v) => {
      if (v?.url) URL.revokeObjectURL(v.url);
      return null;
    });
  };

  const openEdit = (doc) => {
    setEditing(doc);
    setEditForm({
      title: doc.title,
      description: doc.description ?? "",
      category: doc.category ?? "DIGER",
      active: doc.active,
    });
    setFormError(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!editForm.title.trim()) { setFormError("Başlık zorunludur."); return; }
    setSaving(true);
    try {
      await documentService.update(editing.id, {
        title: editForm.title.trim(),
        description: editForm.description.trim() || null,
        category: editForm.category,
        active: editForm.active,
      });
      showToast("Döküman güncellendi.", "success");
      setEditing(null);
      load();
    } catch (err) {
      setFormError(err.response?.data?.error ?? "İşlem başarısız.");
    } finally {
      setSaving(false);
    }
  };

  const triggerDelete = (doc) => {
    setConfirmModal({
      isOpen: true,
      title: "Dökümanı Sil",
      message: `"${doc.title}" dökümanı kalıcı olarak silinecek. Emin misiniz?`,
      type: "danger",
      onConfirm: () =>
        documentService.remove(doc.id)
          .then(() => { showToast("Döküman silindi.", "success"); load(); })
          .catch((err) => showToast(err.response?.data?.error ?? "İşlem başarısız.", "error")),
    });
  };

  const TabButton = ({ id, label, count }) => (
    <button
      onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm font-semibold rounded-full transition ${
        tab === id ? "bg-primary text-white shadow" : "text-on-surface-variant hover:bg-surface-container-high"
      }`}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="folder_open" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Dökümanlar</h1>
            <p className="text-on-surface-variant text-sm">
              {isAdmin
                ? "Tüm salonlara açık sistem dökümanları."
                : "Sistem kılavuzları ve salonunuza ait dökümanlar."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isManager && limit && limit.limit != null && tab === "SALON" && (
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
              limitReached ? "bg-error/10 text-error" : "bg-surface-container-high text-on-surface-variant"
            }`}>
              {limit.used}/{limit.limit} döküman
            </span>
          )}
          {showUploadButton && (
            <button
              onClick={openUpload}
              disabled={isManager && limitReached}
              title={isManager && limitReached ? "Döküman limiti doldu" : "Döküman yükle"}
              className="flex items-center gap-1.5 border border-primary text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-semibold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="upload" className="!text-[18px]" /> Döküman Yükle
            </button>
          )}
        </div>
      </div>

      {/* Sekmeler — ADMIN yalnızca salon değil; platformu yönetir, o yüzden tek liste */}
      {!isAdmin ? (
        <div className="flex items-center gap-2">
          <TabButton id="PLATFORM" label="Sistem Dökümanları" count={platformDocs.length} />
          <TabButton id="SALON" label={isManager ? "Yüklediğim Dökümanlar" : "Salon Dökümanları"} count={salonDocs.length} />
        </div>
      ) : null}

      {isManager && limitReached && tab === "SALON" && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" />
          Döküman yükleme limitiniz doldu ({limit.limit}). Daha fazlası için paketinizi yükseltin.
        </div>
      )}

      {tab === "PLATFORM" && !isAdmin && (
        <p className="text-xs text-on-surface-variant flex items-center gap-1.5">
          <Icon name="info" className="!text-[16px]" /> Bu dökümanlar sistem yöneticisi tarafından paylaşılır; salt-okunurdur.
        </p>
      )}

      {loading ? (
        <div className="p-8 text-center text-on-surface-variant">Yükleniyor…</div>
      ) : (
        <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-hidden">
          {(isAdmin ? platformDocs : shown).length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Icon name="folder_off" className="!text-[40px] text-on-surface-variant/40" />
              <p className="text-sm text-on-surface-variant mt-2">Henüz döküman yok.</p>
            </div>
          ) : (
            (isAdmin ? platformDocs : shown).map((d) => (
              <DocRow
                key={d.id}
                doc={d}
                downloading={downloadingId === d.id}
                viewing={viewingId === d.id}
                onView={handleView}
                onDownload={handleDownload}
                onEdit={openEdit}
                onDelete={triggerDelete}
              />
            ))
          )}
        </div>
      )}

      {/* Görüntüleme (önizleme) modalı — neredeyse tam ekran */}
      {viewer && (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
          <div className="bg-surface-card w-full h-full sm:w-[96vw] sm:h-[95vh] rounded-none sm:rounded-2xl shadow-modal overflow-hidden flex flex-col">
            <div className="px-4 sm:px-6 py-3 border-b border-outline-variant flex items-center justify-between bg-surface-container-low shrink-0">
              <h3 className="text-base font-bold flex items-center gap-2 min-w-0">
                <Icon name={iconForType(viewer.doc.contentType)} className="text-primary shrink-0" />
                <span className="truncate">{viewer.doc.title}</span>
              </h3>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => handleDownload(viewer.doc)} title="İndir" className="p-2 hover:bg-surface-container-high rounded-full text-on-surface-variant hover:text-primary transition">
                  <Icon name="download" className="!text-[20px]" />
                </button>
                <button onClick={closeViewer} title="Kapat" className="p-2 hover:bg-surface-container-high rounded-full"><Icon name="close" /></button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto bg-surface-container-lowest">
              {viewer.kind === "pdf" && (
                <iframe title={viewer.doc.title} src={viewer.url} className="w-full h-full border-0" />
              )}
              {viewer.kind === "image" && (
                <div className="w-full h-full p-4 flex items-center justify-center">
                  <img src={viewer.url} alt={viewer.doc.title} className="max-w-full max-h-full object-contain" />
                </div>
              )}
              {viewer.kind === "text" && (
                <pre className="p-5 text-xs sm:text-sm text-on-surface whitespace-pre-wrap break-words font-mono">{viewer.text}</pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Yükleme modalı */}
      {uploadOpen && (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-surface-card w-full max-w-xl rounded-2xl shadow-modal overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Icon name="upload" className="text-primary" /> Döküman Yükle
              </h3>
              <button onClick={() => setUploadOpen(false)} className="p-2 hover:bg-surface-container-high rounded-full"><Icon name="close" /></button>
            </div>
            <form onSubmit={handleUpload} className="p-6 space-y-4 overflow-y-auto text-sm">
              {formError && (
                <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5">
                  <Icon name="error" className="!text-[16px]" /> {formError}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Dosya *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  onChange={onPickFile}
                  className="w-full text-xs file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                />
                <p className="text-[11px] text-on-surface-variant mt-1">İzin verilen: PDF, Word, Excel, PNG, JPG, CSV · en fazla 10 MB.</p>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Başlık *</label>
                <input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="Örn. Sistem Kullanım Kılavuzu"
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Kategori</label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary"
                >
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Açıklama</label>
                <textarea
                  rows={3}
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Kısa açıklama…"
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setUploadOpen(false)} className="flex items-center gap-1.5 px-5 py-2.5 font-semibold text-on-surface-variant hover:bg-surface-container-high rounded-xl transition text-sm">
                  <Icon name="close" className="!text-[18px]" /> İptal
                </button>
                <button type="submit" disabled={saving} className="flex items-center gap-1.5 px-6 py-2.5 bg-primary text-white font-semibold rounded-xl shadow-lg hover:opacity-90 transition disabled:opacity-50 text-sm">
                  <Icon name="upload" className="!text-[18px]" /> {saving ? "Yükleniyor…" : "Yükle"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Düzenleme modalı (yalnızca metadata) */}
      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-surface-card w-full max-w-xl rounded-2xl shadow-modal overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <h3 className="text-lg font-bold flex items-center gap-2"><Icon name="edit" className="text-primary" /> Dökümanı Düzenle</h3>
              <button onClick={() => setEditing(null)} className="p-2 hover:bg-surface-container-high rounded-full"><Icon name="close" /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4 overflow-y-auto text-sm">
              {formError && (
                <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5">
                  <Icon name="error" className="!text-[16px]" /> {formError}
                </div>
              )}
              <p className="text-xs text-on-surface-variant flex items-center gap-1.5">
                <Icon name="attach_file" className="!text-[16px]" /> {editing.fileName} · {fmtSize(editing.sizeBytes)}
              </p>

              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Başlık *</label>
                <input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Kategori</label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary"
                >
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Açıklama</label>
                <textarea
                  rows={3}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full bg-white border border-outline-variant rounded-xl px-3 py-2.5 outline-none focus:border-primary resize-none"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.active}
                  onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                />
                <span className="text-sm">Aktif (pasif dökümanları personel göremez)</span>
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="flex items-center gap-1.5 px-5 py-2.5 font-semibold text-on-surface-variant hover:bg-surface-container-high rounded-xl transition text-sm">
                  <Icon name="close" className="!text-[18px]" /> İptal
                </button>
                <button type="submit" disabled={saving} className="flex items-center gap-1.5 px-6 py-2.5 bg-primary text-white font-semibold rounded-xl shadow-lg hover:opacity-90 transition disabled:opacity-50 text-sm">
                  <Icon name="check" className="!text-[18px]" /> {saving ? "Kaydediliyor…" : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
